/**
 * ExportModal Component
 *
 * Modal for exporting scheduler data to CSV with filtering options.
 * Features:
 * - Optional company/location filter
 * - Optional guard filter
 * - Required date range filter
 * - Live preview of export data as a table
 * - CSV generation and download
 */

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { fetchLocationsByCompany } from 'services/api/locationService';
import getSchedulesByDateRange from 'api/scheduler/getSchedulesByDateRange';

const ExportModal = ({
  open,
  onClose,
  guards = [],
  companies = [],
  // Default values from calendar
  defaultCompany = '',
  defaultCompanyId = null,
  defaultLocation = '',
  defaultLocationId = null,
  defaultGuard = '',
  defaultGuardId = null,
  defaultStartDate = null,
  defaultEndDate = null,
  viewMode = 'guard',
}) => {
  // Filter state
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedGuard, setSelectedGuard] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Locations for dropdown
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Preview data
  const [previewPatrols, setPreviewPatrols] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Notification hook
  const { notifyError } = useNotificationProvider();

  // Initialize filters when modal opens
  useEffect(() => {
    if (open) {
      // Set company
      if (defaultCompanyId) {
        const companyObj = companies.find(c => c.id === defaultCompanyId);
        setSelectedCompany(companyObj || null);
      } else {
        setSelectedCompany(null);
      }

      // Set guard
      if (defaultGuardId) {
        const guardObj = guards.find(g => g.id === defaultGuardId);
        setSelectedGuard(guardObj || null);
      } else {
        setSelectedGuard(null);
      }

      // Set dates
      if (defaultStartDate) {
        const startStr = defaultStartDate instanceof Date
          ? defaultStartDate.toISOString().split('T')[0]
          : new Date(defaultStartDate).toISOString().split('T')[0];
        setStartDate(startStr);
      } else {
        setStartDate('');
      }

      if (defaultEndDate) {
        const endStr = defaultEndDate instanceof Date
          ? defaultEndDate.toISOString().split('T')[0]
          : new Date(defaultEndDate).toISOString().split('T')[0];
        setEndDate(endStr);
      } else {
        setEndDate('');
      }

      // Reset location and preview
      setSelectedLocation(null);
      setPreviewPatrols([]);
      setPreviewError(null);
    }
  }, [open, defaultCompany, defaultCompanyId, defaultLocation, defaultLocationId, defaultGuard, defaultGuardId, defaultStartDate, defaultEndDate, companies, guards]);

  // Load locations when company changes
  useEffect(() => {
    if (!selectedCompany) {
      setAvailableLocations([]);
      setSelectedLocation(null);
      return;
    }

    let isMounted = true;
    setLoadingLocations(true);

    const loadLocations = async () => {
      try {
        const locations = await fetchLocationsByCompany(selectedCompany.id);
        if (isMounted) {
          setAvailableLocations(locations);
          // Set default location if it matches the selected company
          if (defaultLocationId) {
            const locationObj = locations.find(l => l.id === defaultLocationId);
            setSelectedLocation(locationObj || null);
          }
        }
      } catch (error) {
        console.error('[ExportModal] Failed to load locations:', error);
        if (isMounted) {
          setAvailableLocations([]);
          notifyError('Failed to load locations. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoadingLocations(false);
        }
      }
    };

    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [selectedCompany, defaultLocation, defaultLocationId]);

  // Load preview data when filters change
  useEffect(() => {
    if (!open || !startDate || !endDate) {
      setPreviewPatrols([]);
      return;
    }

    const hasCompanyFilter = selectedCompany !== null;
    const hasGuardFilter = selectedGuard !== null;

    let isMounted = true;
    setLoadingPreview(true);
    setPreviewError(null);

    const loadPreview = async () => {
      try {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        // Build API params based on filters
        const params = {
          startDate: start,
          endDate: end,
        };

        if (hasGuardFilter) {
          params.contactId = selectedGuard.id;
        }
        if (hasCompanyFilter) {
          params.customerId = selectedCompany.id;
        }
        if (selectedLocation) {
          params.addressId = selectedLocation.id;
        }

        const { data: patrols } = await getSchedulesByDateRange(params);

        // If both guard and company filters, the API should handle it
        // but let's also filter client-side to be safe
        let filteredPatrols = patrols;
        if (hasGuardFilter && hasCompanyFilter) {
          filteredPatrols = patrols.filter(p => {
            const matchesCompany = p.CustomerId === selectedCompany.id;
            const matchesLocation = !selectedLocation || p.AddressId === selectedLocation.id;
            return matchesCompany && matchesLocation;
          });
        }

        // Sort by start time
        filteredPatrols.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));

        if (isMounted) {
          setPreviewPatrols(filteredPatrols);
          if (filteredPatrols.length === 0) {
            setPreviewError('No patrols found for the selected date range.');
          }
        }
      } catch (error) {
        console.error('[ExportModal] Failed to load preview:', error);
        if (isMounted) {
          setPreviewPatrols([]);
          setPreviewError('Failed to load preview data. Please try again.');
          notifyError('Failed to load preview data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoadingPreview(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
    };
  }, [open, selectedCompany, selectedLocation, selectedGuard, startDate, endDate]);

  // Handle company change
  const handleCompanyChange = useCallback((_, newValue) => {
    setSelectedCompany(newValue);
    setSelectedLocation(null);
  }, []);

  // Handle location change
  const handleLocationChange = useCallback((_, newValue) => {
    setSelectedLocation(newValue);
  }, []);

  // Handle guard change
  const handleGuardChange = useCallback((_, newValue) => {
    setSelectedGuard(newValue);
  }, []);

  // Check if dates are valid
  const datesValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    return new Date(startDate) <= new Date(endDate);
  }, [startDate, endDate]);

  // Check if export is possible
  const canExport = useMemo(() => {
    return datesValid && previewPatrols.length > 0 && !loadingPreview;
  }, [datesValid, previewPatrols.length, loadingPreview]);

  // Format patrol for display
  const formatPatrolRow = useCallback((patrol) => {
    const startDate = new Date(patrol.StartTime);
    const endDate = new Date(patrol.EndTime);
    const durationHours = ((endDate - startDate) / (1000 * 60 * 60)).toFixed(1);

    return {
      guard: patrol.ContactName || '',
      company: patrol.CustomerName || '',
      location: patrol.AddressName || '',
      date: startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      startTime: startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      endTime: endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      duration: `${durationHours}h`,
    };
  }, []);

  // Generate CSV content
  const generateCsvContent = useCallback((patrols) => {
    const headers = ['Guard', 'Company', 'Location', 'Date', 'Start Time', 'End Time', 'Duration (hours)'];

    const rows = patrols.map((patrol) => {
      const startDate = new Date(patrol.StartTime);
      const endDate = new Date(patrol.EndTime);
      const durationHours = ((endDate - startDate) / (1000 * 60 * 60)).toFixed(1);

      return [
        patrol.ContactName || '',
        patrol.CustomerName || '',
        patrol.AddressName || '',
        startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
        startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        durationHours,
      ];
    });

    const escapeValue = (value) => {
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvLines = [
      headers.map(escapeValue).join(','),
      ...rows.map(row => row.map(escapeValue).join(',')),
    ];

    return csvLines.join('\n');
  }, []);

  // Generate filename
  const generateFilename = useCallback(() => {
    const startStr = startDate;
    const endStr = endDate;

    let filterParts = [];
    if (selectedGuard) {
      filterParts.push(selectedGuard.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase());
    }
    if (selectedCompany) {
      filterParts.push(selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase());
    }
    if (selectedLocation) {
      filterParts.push(selectedLocation.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase());
    }

    const filterStr = filterParts.length > 0 ? filterParts.join('-') : 'all';

    return `schedule-${filterStr}-${startStr}-to-${endStr}.csv`;
  }, [startDate, endDate, selectedGuard, selectedCompany, selectedLocation]);

  // Handle export
  const handleExport = useCallback(() => {
    if (previewPatrols.length === 0) return;

    try {
      const csvContent = generateCsvContent(previewPatrols);
      const filename = generateFilename();

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onClose();
    } catch (error) {
      console.error('[ExportModal] Export failed:', error);
      notifyError('Failed to export CSV. Please try again.');
    }
  }, [previewPatrols, generateCsvContent, generateFilename, onClose, notifyError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownloadIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Export Schedule
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Filters Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Filters
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Date Range Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileDatePicker
                  label="Start Date"
                  value={startDate ? new Date(startDate) : null}
                  onChange={(newDate) => {
                    setStartDate(newDate ? newDate.toISOString().split('T')[0] : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      required
                      error={startDate && endDate && !datesValid}
                      helperText={startDate && endDate && !datesValid ? 'Start must be before end' : ''}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <MobileDatePicker
                  label="End Date"
                  value={endDate ? new Date(endDate) : null}
                  onChange={(newDate) => {
                    setEndDate(newDate ? newDate.toISOString().split('T')[0] : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      required
                      error={startDate && endDate && !datesValid}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            {/* Company and Location Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                value={selectedCompany}
                onChange={handleCompanyChange}
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company (optional)"
                    placeholder="All companies"
                    size="small"
                  />
                )}
                sx={{ flex: 1 }}
                size="small"
              />

              <Tooltip
                title={!selectedCompany ? 'Select a company first' : ''}
                disableHoverListener={!!selectedCompany}
              >
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    value={selectedLocation}
                    onChange={handleLocationChange}
                    options={availableLocations}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!selectedCompany}
                    loading={loadingLocations}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Location (optional)"
                        placeholder={selectedCompany ? 'All locations' : 'Select company first'}
                        size="small"
                      />
                    )}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                </Box>
              </Tooltip>
            </Box>

            {/* Guard Row */}
            <Autocomplete
              value={selectedGuard}
              onChange={handleGuardChange}
              options={guards}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Guard (optional)"
                  placeholder="All guards"
                  size="small"
                />
              )}
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Preview Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Preview
            </Typography>
            {previewPatrols.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {previewPatrols.length} patrol{previewPatrols.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : previewError ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          ) : previewPatrols.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Guard</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewPatrols.slice(0, 50).map((patrol, index) => {
                    const row = formatPatrolRow(patrol);
                    return (
                      <TableRow key={patrol.ScheduleAssignmentId || index}>
                        <TableCell>{row.guard}</TableCell>
                        <TableCell>{row.company}</TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.startTime}</TableCell>
                        <TableCell>{row.endTime}</TableCell>
                        <TableCell>{row.duration}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {previewPatrols.length > 50 && (
                <Box sx={{ p: 1, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing 50 of {previewPatrols.length} patrols. All patrols will be included in the export.
                  </Typography>
                </Box>
              )}
            </TableContainer>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Select a date range to preview data.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          color="primary"
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={!canExport}
        >
          Export {previewPatrols.length > 0 ? `(${previewPatrols.length})` : ''} CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  guards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  companies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  defaultCompany: PropTypes.string,
  defaultCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultLocation: PropTypes.string,
  defaultLocationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultGuard: PropTypes.string,
  defaultGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  defaultEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  viewMode: PropTypes.oneOf(['company', 'guard']),
};

export default memo(ExportModal);
